import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Skeleton,
  Chip,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TokenIcon from '@mui/icons-material/Token';
import SettingsIcon from '@mui/icons-material/Settings';
import SecurityIcon from '@mui/icons-material/Security';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import PublicIcon from '@mui/icons-material/Public';
import { getAuditLogs } from 'services/transparency-config-service';
import { formatDateTimeWithTimezone } from 'utils/date_utils';
import { LOCALE_MAP } from 'utils/locale-formatting';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

/**
 * Event type configuration with icons — labels resolved via t() at render time
 */
const EVENT_TYPE_META = {
  token_created: { icon: TokenIcon, color: 'success' },
  token_used: { icon: VisibilityIcon, color: 'info' },
  token_revoked: { icon: BlockIcon, color: 'warning' },
  public_access: { icon: PublicIcon, color: 'info' },
  access_denied: { icon: CancelIcon, color: 'error' },
  general_settings: { icon: SettingsIcon, color: 'default' },
  framework_visibility: { icon: SettingsIcon, color: 'default' },
  policy_visibility: { icon: SettingsIcon, color: 'default' },
  access_control: { icon: SecurityIcon, color: 'default' },
};

/**
 * Audit Log Viewer
 * Display audit trail of configuration changes and token usage
 */
export const AuditLog = ({ companyKey }) => {
  const selectedTenant = useSelector(useAuthTenant);
  const companyLocale = LOCALE_MAP[selectedTenant?.defaultLanguage] ?? 'pt-BR';
  const { t } = useTranslation(['transparency', 'common']);

  // Filter state
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    eventType: '',
    page: 1,
    perPage: 50,
  });

  // Query audit logs
  const { data, isLoading, error } = useQuery(
    ['auditLogs', companyKey, filters],
    () => getAuditLogs(companyKey, filters),
    {
      enabled: !!companyKey,
      keepPreviousData: true,
      staleTime: 30 * 1000, // Cache for 30 seconds
    }
  );

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
      page: key !== 'page' ? 1 : value, // Reset to page 1 when filters change
    });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      eventType: '',
      page: 1,
      perPage: 50,
    });
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    handleFilterChange('page', newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    handleFilterChange('perPage', parseInt(event.target.value, 10));
  };

  // Get event type configuration with translated label
  const getEventConfig = (eventType) => {
    const meta = EVENT_TYPE_META[eventType];
    if (!meta) return { label: eventType, icon: SettingsIcon, color: 'default' };
    return { ...meta, label: t(`transparency:auditLog.events.${eventType}`) };
  };

  // Loading state
  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          {t('transparency:auditLog.error')}
        </Alert>
      </Paper>
    );
  }

  const logs = data?.data?.logs || [];
  const total = data?.data?.total || 0;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('transparency:auditLog.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('transparency:auditLog.subtitle')}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('transparency:auditLog.filters.title')}
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={t('transparency:auditLog.filters.startDate')}
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label={t('transparency:auditLog.filters.endDate')}
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('transparency:auditLog.filters.eventType')}</InputLabel>
            <Select
              value={filters.eventType}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
              label={t('transparency:auditLog.filters.eventType')}
            >
              <MenuItem value="">{t('transparency:auditLog.filters.allEvents')}</MenuItem>
              {Object.keys(EVENT_TYPE_META).map((key) => (
                <MenuItem key={key} value={key}>
                  {t(`transparency:auditLog.events.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={clearFilters}>
            {t('transparency:auditLog.filters.clear')}
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Logs Table */}
      {logs.length > 0 ? (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('transparency:auditLog.table.datetime')}</TableCell>
                  <TableCell>{t('transparency:auditLog.table.eventType')}</TableCell>
                  <TableCell>{t('transparency:auditLog.table.actor')}</TableCell>
                  <TableCell>{t('transparency:auditLog.table.recipientEmail')}</TableCell>
                  <TableCell align="center">{t('transparency:auditLog.table.status')}</TableCell>
                  <TableCell>{t('transparency:auditLog.table.details')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, index) => {
                  const eventConfig = getEventConfig(log.eventType);
                  const EventIcon = eventConfig.icon;
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {formatDateTimeWithTimezone(log.timestamp, selectedTenant?.timezone, companyLocale)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon fontSize="small" />
                          <Chip
                            label={eventConfig.label}
                            color={eventConfig.color}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.actorEmail || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.recipientEmail || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {log.success ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={log.success ? 'text.secondary' : 'error'}
                        >
                          {log.failureReason || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={total}
            page={filters.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={filters.perPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage={t('transparency:auditLog.pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${t('transparency:auditLog.pagination.of')} ${count !== -1 ? count : `${t('transparency:auditLog.pagination.moreThan')} ${to}`}`
            }
          />
        </>
      ) : (
        <Alert severity="info">
          {t('transparency:auditLog.empty')}
        </Alert>
      )}
    </Paper>
  );
};
